import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { formattedDateTime, getCurrentDateTime } from 'pages/utils/commonUtils';
import { PrimaryButton, PrimaryPinkButton, WhiteButton } from "components/common/Button";
import { HappyCallTemplate } from "type/type";
import { maxLengthCheck, getHappyCallTemplateOne, postHappyCallTemplate, putHappyCallTemplate, deleteHappyCallTemplate } from "api/api";
import Loading from "components/common/Loading";
import { useForm } from "react-hook-form";


const Container = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 0.5rem;
  > div {
    padding: 0.5rem 1rem 1rem;
    &:last-of-type {
      background-color: ${prop => prop.theme.layer3BgColor};
      border-radius: 0.5rem;
    }
  }
  .title-h2 {
    font-family: var(--font-pretendard-bold);
    display: inline-flex;
    flex-direction: row;
    gap: 0.5rem;
    align-items: center;
  }

  .process-content {
    > div {
      max-width: 700px;
    }
    input {
      min-width: 150px;
      max-width: 400px;
      // width: 50%;
    }

    .select-wrap {
      position: relative;
      min-width: 150px;
      max-width: 400px;
      width: 100%;

      &::after {
        content: '';
        position: absolute;
        width: 0;
        height: 0;
        top: 50%;
        right: 20px;
        border: 5px solid transparent;
        border-top-color: #313a43;
        transform: scaleY(1.4);
      }
      &::before {
        content: '';
        position: absolute;
        display: block;
        width: 1px;
        height: 95%;
        top: 0;
        right: 48px;
        background-color: #e7e7e7;
        z-index: 0;
      }
    }
  }
  
  .process-content2 {
    > div {
      max-width: 1400px;
    }
  }

  .err-border {
    border: 1px solid red !important;
  }
`;

const TitleAndBtnContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  > div {
    display: flex;
    gap: 0.5rem;
    &:first-of-type {
      flex-direction: column;
    }
    &:last-of-type {
      align-items: center;
    }
  }
`;

const FlexCol = styled.div`
  display: flex;
  flex-direction: column;
`;
const FlexRow = styled.div`
  display: flex;
  flex-direction: row;
`;

const InputWrap = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0.5rem;
  gap: 1rem;
  // FIXME: 다른 페이지 디자인대로 하려면 아래 주석 풀면 됨
  // border-bottom: 1px solid #666;

  label {
    width: 6rem;
  }
  label, input, select, textarea {
    display: inline-block;
    padding: 0.5rem;
  }
  textarea {
    resize: none;
    min-height: 10rem;
  }
  input:not([type="radio"]), textarea {
    width: calc(100% - 6rem);
    border: none;
    background-color: #f1f1f1;
    border-radius: 0.25rem;
    &:focus-visible {
      outline: none;
      &:not(:disabled, :read-only) {
        box-shadow: inset -1px -1px 1px 1px ${prop => prop.theme.primaryColor};
      }
    }
    &:read-only {
      background-color: inherit;
      border-bottom: 1px solid #666;
      border-radius: 0;
      // FIXME: 다른 페이지 디자인대로 하려면 아래 주석 풀면 됨
      // border-radius: 0.25rem;
      // background-color: ${prop => prop.theme.readOnlyColor}; 
      color: ${prop => prop.theme.rootTextColor};
      cursor: auto;
    }
  }
`;

function AfterServiceProcess() {
  // 등록/수정 시 전달 값, url
  const navigate = useNavigate();
  // 목록에서 전달 받은 값
  const { happyCallTemplateIdx } = useParams();
  const [loading, setLoading] = useState(true);
  
  // useForm
  const { register, formState: { errors, isValid }, trigger } = useForm<any>({
    mode: 'all',
  });

  const [happyCallTemplateData, setHappyCallTemplateData] = useState<HappyCallTemplate>({
    idx: happyCallTemplateIdx ? Number(happyCallTemplateIdx) : 0,
    code: '',
    type: '',
    message: '',
    create_date: '',
    update_date: '',
  });
  
  // HAPPY CALL TEMPLATE 정보 변경
  const handleChange = (event: any) => {
    maxLengthCheck(event);
    const { name, value } = event.target;
    setHappyCallTemplateData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  
  // HAPPY CALL TEMPLATE 등록
  const _createHappyCallTemplate = async () => {
    if (!isValid) {
      await trigger();
      if (!happyCallTemplateData.type) return alert('분류를 선택해주세요.');
      if (!happyCallTemplateData.message) return alert('메시지를 입력해주세요.');
    }

    const response = await postHappyCallTemplate(happyCallTemplateData);
    if (response.code === 200) {
      alert("해피콜 템플릿이 등록되었습니다.");
      navigate(-1);
    } else {
      alert(response.message);
    }
  };
  
  // HAPPY CALL TEMPLATE 삭제
  const _deleteHappyCallTemplate = async () => {
    const isDel = window.confirm(`해당 템플릿을 정말 삭제하시겠습니까?`);
    if (isDel) {
      const response = await deleteHappyCallTemplate(Number(happyCallTemplateIdx));
      if (response.code === 200) {
        alert("해피콜 템플릿이 삭제되었습니다..");
        navigate(-1);
      } else {
        alert(response.message);
      }
    }
  };

  // HAPPY CALL TEMPLATE 수정
  const _editHappyCallTemplate = async () => {
    if (!isValid) {
      trigger();
      return;
    }

    const response = await putHappyCallTemplate(happyCallTemplateData);
    if (response.code === 200) {
      alert("해피콜 템플릿이 수정되었습니다.");
      navigate(-1);
    } else {
      alert(response.message);
    }
  };

  // AS 리스트 가져오기
  const getHappyCallTemplate = useCallback(async () => {
    try {
      setLoading(true); // 🔥 요청 시작 전에 딩 설정
      if (!happyCallTemplateIdx) {
        return;
      }
      const happyCallTemplateRes = await getHappyCallTemplateOne(Number(happyCallTemplateIdx));
      if (happyCallTemplateRes.code === 200) {
        setHappyCallTemplateData(happyCallTemplateRes.data);
      } else {
        alert(happyCallTemplateRes.message);
      }
  
    } catch (error) {
      console.error("error", error);
    } finally {
      setLoading(false); // 🔥 요청이 끝나면 무조건 로딩 종료
    }
  }, [happyCallTemplateIdx, navigate]);

  // 처음 useEffect 실행 및 페이지 변경 시 실행
  useEffect(() => {
    getHappyCallTemplate();
  }, [getHappyCallTemplate]);
  return (
    <>
    {loading ? <Loading /> :
      <Container>
        <TitleAndBtnContainer>
          <div>
            <p className='title-h2'>
              <span></span>
            </p>
          </div>
          <div>
            {!happyCallTemplateIdx ?
              <PrimaryButton type="button" onClick={_createHappyCallTemplate}>저장</PrimaryButton>
            :
              <>
                <PrimaryButton type="button" onClick={_editHappyCallTemplate}>저장</PrimaryButton>
                <PrimaryPinkButton type="button" onClick={_deleteHappyCallTemplate}>삭제</PrimaryPinkButton>
              </>
            }
            <WhiteButton type="button" onClick={() => navigate(-1)}>목록</WhiteButton>
          </div>
        </TitleAndBtnContainer>
        <FlexCol>
          <FlexRow className="process-content">
            <InputWrap>
              <label>분류</label>
              <div className="select-wrap">
                <select value={happyCallTemplateData.type}
                  {...register("type", {
                    required: true,
                    onChange: handleChange,
                  })}
                >
                  <option value="" disabled>분류를 선택해주세요.</option>
                  <option value="기본">기본</option>
                  <option value="내부 이벤트 없음">내부 이벤트 없음</option>
                  <option value="외부 이벤트 없음">외부 이벤트 없음</option>
                  <option value="이벤트 발생">이벤트 발생</option>
                  <option value="전체시간 대기반">전체시간 대기반</option>
                  <option value="잠금 보안 이벤트 없음">잠금 보안 이벤트 없음</option>
                </select>
              </div>
            </InputWrap>
            {happyCallTemplateData.create_date &&
              <InputWrap>
                <label>등록일자</label>
                <input type='text' value={ formattedDateTime(happyCallTemplateData.create_date) } readOnly />
              </InputWrap>
            }
          </FlexRow>
          <FlexRow className="process-content">
            <InputWrap>
              <label>코드</label>
              <input type="text" value={ happyCallTemplateData.code } className={`${errors.code && 'err-border'}`} placeholder="NOTI-talk의 알림톡 템플릿 코드와 동일하게 입력해주세요." maxLength={100}
                {...register("code", {
                  required: true,
                  onChange: handleChange
                })}
              />
            </InputWrap>
          </FlexRow>
          <FlexRow className="process-content">
            <InputWrap>
              <label>메시지</label>
              <textarea value={ happyCallTemplateData.message } className={`${errors.message && 'err-border'}`} placeholder="메시지 내용을 입력해주세요. 단, NOTI-talk의 알림톡 템플릿과 동일해야합니다." maxLength={500}
                {...register("message", {
                  required: true,
                  onChange: handleChange
                })}
              ></textarea>
            </InputWrap>
          </FlexRow>
        </FlexCol>
      </Container>
    }
    </>
  );
}

export default AfterServiceProcess;